import { useRef, type ChangeEvent, type ClipboardEvent, type KeyboardEvent } from "react";

interface OtpInputProps {
  value: string;
  onChange: (value: string) => void;
  /** Fired once the last digit lands — the student never taps "verify". */
  onComplete: (code: string) => void;
  length?: number | undefined;
  invalid?: boolean | undefined;
  disabled?: boolean | undefined;
}

/**
 * Six boxes that behave like one field.
 *
 * Auto-advance forward, auto-back on delete, auto-submit on the last digit —
 * the spec is explicit that there is no verify button, because on a 2G
 * connection an extra tap is an extra chance to lose the session.
 *
 * Pasting a whole code works: Android's SMS autofill delivers all six digits
 * into whichever box has focus, and without this that code lands as one digit
 * and five dropped characters.
 */
export function OtpInput({
  value,
  onChange,
  onComplete,
  length = 6,
  invalid = false,
  disabled = false,
}: OtpInputProps) {
  const boxes = useRef<(HTMLInputElement | null)[]>([]);

  const commit = (next: string) => {
    onChange(next);
    if (next.length === length) onComplete(next);
  };

  const focusBox = (index: number) => {
    boxes.current[Math.max(0, Math.min(index, length - 1))]?.focus();
  };

  const handleChange = (index: number) => (event: ChangeEvent<HTMLInputElement>) => {
    const digits = event.target.value.replace(/\D/g, "");
    if (!digits) return;

    // Autofill can deliver the whole code into one box.
    const next = (value.slice(0, index) + digits).slice(0, length);
    commit(next);
    focusBox(next.length);
  };

  const handleKeyDown = (index: number) => (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Backspace") {
      event.preventDefault();
      // Delete the digit under the cursor, or step back if this box is empty.
      const target = value[index] ? index : index - 1;
      if (target < 0) return;
      commit(value.slice(0, target));
      focusBox(target);
      return;
    }
    if (event.key === "ArrowLeft") focusBox(index - 1);
    if (event.key === "ArrowRight") focusBox(index + 1);
  };

  const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    const digits = event.clipboardData.getData("text").replace(/\D/g, "").slice(0, length);
    if (!digits) return;
    commit(digits);
    focusBox(digits.length);
  };

  return (
    <div className={`flex gap-2 ${invalid ? "animate-[shake_0.4s]" : ""}`} data-invalid={invalid}>
      {Array.from({ length }, (_, index) => (
        <input
          key={index}
          ref={(element) => {
            boxes.current[index] = element;
          }}
          // One character per box, but `text` not `number` — a number input
          // shows spinners and lets `e`/`-` through on some Android keyboards.
          type="text"
          inputMode="numeric"
          autoComplete={index === 0 ? "one-time-code" : "off"}
          maxLength={1}
          disabled={disabled}
          aria-label={`Digit ${index + 1} of ${length}`}
          aria-invalid={invalid}
          value={value[index] ?? ""}
          onChange={handleChange(index)}
          onKeyDown={handleKeyDown(index)}
          onPaste={handlePaste}
          className={`tabular h-14 w-full min-w-0 rounded-[var(--radius-control)] border bg-surface
            text-center text-[22px] font-semibold text-text outline-none
            focus:border-accent disabled:opacity-50
            ${invalid ? "border-negative" : "border-border-strong"}`}
        />
      ))}
    </div>
  );
}
